import { useEffect, useMemo, useRef, useState } from 'react';
import { BRAND_META, BRAND_ORDER } from '../brand';
import type { ViewKey } from './Header';
import type { BrandName, Me } from '../types';

/**
 * Cmd+K / Ctrl+K: the keyboard door to everything the Shell can show or
 * mint (1 September 2026). Navigation and deep links only - it can do
 * nothing the buttons cannot, it just does it without the mouse. RBAC is
 * unchanged: the palette offers exactly the views and admins this
 * principal could already reach, and the server enforces either way.
 */

interface Command {
  id: string;
  label: string;
  hint: string;
  run: () => void;
}

export function CommandPalette({
  me,
  onView,
  onDeepLink,
}: {
  me: Me;
  onView: (v: ViewKey) => void;
  onDeepLink: (brand: BrandName, entity: string, id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const commands = useMemo<Command[]>(() => {
    const close = (fn: () => void) => () => {
      setOpen(false);
      fn();
    };
    const list: Command[] = [];
    if (me.role === 'owner' || me.role === 'analyst') {
      list.push({ id: 'view-group', label: 'Group view', hint: 'view', run: close(() => onView('group')) });
    }
    for (const b of BRAND_ORDER.filter((b) => me.brands.includes(b))) {
      list.push({
        id: `view-${b}`,
        label: `${BRAND_META[b].label} view`,
        hint: 'view',
        run: close(() => onView(b)),
      });
    }
    list.push({ id: 'view-competitors', label: 'Competitors', hint: 'view', run: close(() => onView('competitors')) });
    list.push({ id: 'view-chat', label: 'Chat — ask an agent', hint: 'view', run: close(() => onView('chat')) });
    if (me.role === 'owner' || me.role === 'operator') {
      list.push({ id: 'view-approvals', label: 'Approval queue', hint: 'view', run: close(() => onView('approvals')) });
      for (const b of BRAND_ORDER.filter((b) => me.brands.includes(b))) {
        list.push({
          id: `admin-${b}`,
          label: `Open ${BRAND_META[b].label} admin`,
          hint: 'deep link',
          run: close(() => onDeepLink(b, 'home', null)),
        });
      }
    }
    return list;
  }, [me, onView, onDeepLink]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? commands.filter((c) => c.label.toLowerCase().includes(q)) : commands;
  }, [commands, query]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
        setQuery('');
        setIndex(0);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div className="palette-scrim" onClick={() => setOpen(false)}>
      <div className="palette" role="dialog" aria-label="Command palette" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          className="palette-input"
          placeholder="Jump to a view, open an admin…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIndex(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setIndex((i) => Math.min(matches.length - 1, i + 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setIndex((i) => Math.max(0, i - 1));
            } else if (e.key === 'Enter') {
              matches[index]?.run();
            }
          }}
        />
        <ul className="palette-list" role="listbox">
          {matches.length === 0 && <li className="palette-empty">Nothing matches.</li>}
          {matches.map((c, i) => (
            <li key={c.id}>
              <button
                role="option"
                aria-selected={i === index}
                className={i === index ? 'selected' : undefined}
                onMouseEnter={() => setIndex(i)}
                onClick={c.run}
              >
                {c.label}
                <span className="palette-hint">{c.hint}</span>
              </button>
            </li>
          ))}
        </ul>
        <div className="palette-legend">
          <kbd>↑</kbd>/<kbd>↓</kbd> move · <kbd>↵</kbd> go · <kbd>esc</kbd> close
        </div>
      </div>
    </div>
  );
}
